import { useState, type KeyboardEvent, type ReactElement } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Code,
  LocationIcon,
  SearchIcon,
  Skeleton,
  Stack,
  Text,
  TextField,
} from '@citibox-panel/ui';
import { parseLocationId } from '../lib/locationId';
import { useDoorAccessLocation } from '../hooks/useDoorAccessLocation';
import { type DoorAccessLocation } from '../lib/doorAccessLocation';
import { ReadErrorStrip } from '../../../components/ReadErrorStrip';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface LookupCardProps {
  // Injected by the page, like the two on the cards beside it: the module imports no client and
  // does not know whether it is talking to a mock.
  fetchLocation: (locationId: number) => Promise<DoorAccessLocation>;
  isHandledByHost?: IsHandledByHost;
}

// TODO: i18n — copy en ES hasta Fase 1.
const PLACEHOLDER = 'ID de ubicación';
const EXAMPLE_HINT = 'Ej. 8433';
const INVALID_HINT = 'El ID es un número, sin letras ni guiones';
// Fixed panel copy. The server's own `message`/`detail` never reaches the DOM — an error envelope is
// attacker-influenced data like any other payload — and the card switches on no `code` string
// either, because the codes on this route are lowercase while other endpoints send them uppercase.
const READ_ERROR = 'No se ha podido consultar esta ubicación.';
// The caveat exists because the endpoint never 404s: with no row, «disabled» and «this id is no
// location at all» answer exactly the same, so the screen says both instead of picking one.
const NOT_CONFIGURED = 'Sin configuración previa — comprueba que el ID existe';
// The width of the answer it stands in for, so the card does not jump when the read lands.
const SKELETON_WIDTH = 180;

// Look one location up at the gate. READ-ONLY by decision: every write on this screen goes through
// the batch card, so there is one write path, one confirmation and one invalidation point.
//
// TODO: i18n — copy en ES hasta Fase 1.
export function LookupCard({
  fetchLocation,
  isHandledByHost = neverHandledByHost,
}: LookupCardProps): ReactElement {
  const [raw, setRaw] = useState('');
  // The id that has been SUBMITTED, which is not the one being typed: typing does not read.
  const [submittedId, setSubmittedId] = useState<number | null>(null);

  // Trimmed before it is filtered: an id copied out of a sheet or a chat arrives with whitespace,
  // and calling `8433 ` «not a number» would be a lie. The rule itself stays strict.
  const candidate = parseLocationId(raw.trim());
  // An empty field is not mistyped, it is just empty.
  const isInvalid = raw.trim() !== '' && candidate === null;

  const { location, isFetching, isError, error, retry } = useDoorAccessLocation(
    fetchLocation,
    submittedId,
  );
  // An error the host already shows is not the card's to report. The answer below still hides on the
  // raw error, so a refused read never leaves an earlier answer standing as if it were current.
  const showsError = isError && !isHandledByHost(error);

  const submit = () => {
    // No error branch: the button is disabled and Enter does nothing while there is no candidate,
    // so this is never reached with `null`.
    if (candidate !== null) setSubmittedId(candidate);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter looks up. In the field beside it Enter is the separator and submits NOTHING — two
    // keyboard contracts on one screen, each the right one for its field.
    if (event.key === 'Enter') submit();
  };

  return (
    // Elevated at level 4, matching its two siblings: all three sit ON the page's white panel, where
    // a hairline border would vanish into the surface behind them.
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <LocationIcon fontSize="small" />
          {/* `component="h2"` is what puts it in the document outline: with three cards on the
              page, heading navigation is how an AT user moves between them. */}
          <Text variant="subtitle2" component="h2" bold>
            Consultar una ubicación
          </Text>
        </Box>
        <TextField
          value={raw}
          onChange={setRaw}
          onKeyDown={handleKeyDown}
          placeholder={PLACEHOLDER}
          // The placeholder is NOT an accessible name: it disappears on the first keystroke and not
          // every screen reader announces it. Same text, so there are not two to keep in sync.
          aria-label={PLACEHOLDER}
          startAdornment={<SearchIcon fontSize="small" />}
          helperText={isInvalid ? INVALID_HINT : EXAMPLE_HINT}
          error={isInvalid}
          // The face `docs/DESIGN.md` reserves for ids, and the one the field beside it already has.
          monospace
          fullWidth
        />
        <Stack direction="row" sx={{ mt: 2 }}>
          {/* One condition covers both cases that cannot be looked up — empty and malformed:
              neither of them yields a candidate. */}
          <Button disabled={candidate === null} onClick={submit}>
            CONSULTAR
          </Button>
        </Stack>
        {/* Mounted in every state, so the region exists BEFORE its content changes — a region that
            arrives with its text is the update assistive tech routinely misses. */}
        {/* The skeleton covers a RE-read too (a retry, or the invalidation a wave written beside it
            causes) rather than leaving an answer on screen while it is being revalidated. */}
        <Box role="status" sx={{ mt: 2 }}>
          {isFetching && <Skeleton variant="text" width={SKELETON_WIDTH} />}
          {location !== undefined && !isError && !isFetching && (
            <>
              {/* The «·» of the sketch is the Stack's gap: the state is a capsule, not a word
                  inside the sentence.

                  The id is echoed INSIDE a three-word sentence rather than standing alone, because
                  that is what `Code` is by its own contract — an interruption of prose, not a block
                  of type — and it is how the panel's only other caller uses it. Alone beside a
                  capsule the two would compete; in a sentence the number also says what it is a
                  number of. */}
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Text variant="body2">
                  Ubicación <Code>{String(location.locationId)}</Code>
                </Text>
                <Chip
                  label={location.enabled ? 'Activada' : 'Desactivada'}
                  // `default` and never `error`: disabled is the network's default state, not a
                  // failure of anything.
                  color={location.enabled ? 'success' : 'default'}
                />
              </Stack>
              {!location.configured && (
                <Box sx={{ mt: 0.5 }}>
                  {/* Body tone, no severity: it is a caveat about an id, not an error. The endpoint
                      did not fail — it answered. */}
                  <Text variant="body2">{NOT_CONFIGURED}</Text>
                </Box>
              )}
            </>
          )}
        </Box>
        {/* Outside the region on purpose: the strip carries a control, and a control inside a region
            that is read aloud becomes part of the announcement — leaving the reader to go hunting
            for the button they were just told about. Same placement, same reason, as the batch
            card's alert. */}
        {showsError && (
          <Box sx={{ mt: 2 }}>
            <ReadErrorStrip message={READ_ERROR} onRetry={retry} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
